/**
 * MẠNG CỦA CÔNG TY (05/10/2026, chủ dự án: «chấm công chỉ cho phép mạng nội bộ công ty mới được chấm công
 * được») — đọc, chuẩn hoá và so địa chỉ mạng IPv4 / IPv6. Không thư viện; dùng chung cho máy chủ (chặn chấm
 * công ngoài mạng công ty) và web (ô nhập ở Cài đặt).
 *
 * Mọi máy trong văn phòng ra Internet qua CÙNG MỘT địa chỉ IPv4 công cộng của đường mạng công ty, nên «mạng
 * công ty» là địa chỉ ấy. Riêng IPv6 thì mỗi máy một địa chỉ (lại tự đổi theo giờ), nhưng cùng chung một
 * DẢI /64 của mạng nội bộ — nên với IPv6 phải khai cả dải. Tên miền của app có cả IPv6 (bản ghi AAAA của lớp
 * «Bảo vệ» VNPT), máy nào có IPv6 sẽ đi đường ấy: danh sách mạng công ty thường cần cả hai loại.
 */
const WIDTH = { 4: 32, 6: 128 };
function parseIPv4(text) {
    const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(text);
    if (!match)
        return null;
    let value = 0n;
    for (const part of match.slice(1)) {
        const octet = Number(part);
        if (octet > 255 || (part.length > 1 && part.startsWith('0')))
            return null;
        value = (value << 8n) | BigInt(octet);
    }
    return value;
}
function parseIPv6(input) {
    let text = input.toLowerCase();
    // Bỏ phần vùng «%eth0» nếu có.
    const zone = text.indexOf('%');
    if (zone >= 0)
        text = text.slice(0, zone);
    // Đuôi IPv4 nhúng: «::ffff:1.2.3.4».
    if (text.includes('.')) {
        const cut = text.lastIndexOf(':');
        const v4 = parseIPv4(text.slice(cut + 1));
        if (v4 === null)
            return null;
        text = `${text.slice(0, cut + 1)}${(v4 >> 16n).toString(16)}:${(v4 & 0xffffn).toString(16)}`;
    }
    const halves = text.split('::');
    if (halves.length > 2)
        return null;
    const head = halves[0] ? halves[0].split(':') : [];
    const tail = halves.length === 2 && halves[1] ? halves[1].split(':') : [];
    const missing = 8 - head.length - tail.length;
    if (halves.length === 1 ? head.length !== 8 : missing < 1)
        return null;
    const groups = [...head, ...Array(halves.length === 2 ? missing : 0).fill('0'), ...tail];
    let value = 0n;
    for (const group of groups) {
        if (!/^[0-9a-f]{1,4}$/.test(group))
            return null;
        value = (value << 16n) | BigInt(parseInt(group, 16));
    }
    return value;
}
/** Dải IPv6 chứa địa chỉ IPv4 «gói trong IPv6» (::ffff:0:0/96) — coi như IPv4. */
const MAPPED_PREFIX = 0xffffn;
/** Đọc một địa chỉ (IPv4, IPv6, IPv4 gói trong IPv6); `null` = không phải địa chỉ. */
export function parseAddress(text) {
    const trimmed = text.trim();
    const v4 = parseIPv4(trimmed);
    if (v4 !== null)
        return { family: 4, value: v4 };
    const v6 = parseIPv6(trimmed);
    if (v6 === null)
        return null;
    if (v6 >> 32n === MAPPED_PREFIX)
        return { family: 4, value: v6 & 0xffffffffn };
    return { family: 6, value: v6 };
}
const maskOf = (family, prefix) => {
    const width = BigInt(WIDTH[family]);
    return prefix === 0 ? 0n : ((1n << BigInt(prefix)) - 1n) << (width - BigInt(prefix));
};
/** Đọc một mạng: «a.b.c.d», «a.b.c.d/24», địa chỉ IPv6, «2402:800::/64». Phần máy bị xoá về 0. */
export function parseNetwork(text) {
    const [base = '', bits, extra] = text.trim().split('/');
    if (extra !== undefined)
        return null;
    const address = parseAddress(base);
    if (!address)
        return null;
    const width = WIDTH[address.family];
    let prefix = width;
    if (bits !== undefined) {
        if (!/^\d{1,3}$/.test(bits))
            return null;
        prefix = Number(bits);
        // IPv4 viết trong IPv6 kèm tiền tố tính theo IPv6 — không nhận cho khỏi lẫn.
        if (prefix > width || (address.family === 4 && base.includes(':')))
            return null;
    }
    return { ...address, prefix, value: address.value & maskOf(address.family, prefix) };
}
function formatIPv4(value) {
    return [24n, 16n, 8n, 0n].map((shift) => ((value >> shift) & 0xffn).toString()).join('.');
}
/** IPv6 viết gọn theo RFC 5952: chữ thường, bỏ số 0 đầu nhóm, dãy nhóm 0 dài nhất thành «::». */
function formatIPv6(value) {
    const groups = Array.from({ length: 8 }, (_, index) => Number((value >> BigInt((7 - index) * 16)) & 0xffffn));
    let bestStart = -1;
    let bestLength = 0;
    for (let index = 0; index < 8;) {
        if (groups[index] !== 0) {
            index += 1;
            continue;
        }
        let end = index;
        while (end < 8 && groups[end] === 0)
            end += 1;
        if (end - index > bestLength) {
            bestStart = index;
            bestLength = end - index;
        }
        index = end;
    }
    const hex = groups.map((group) => group.toString(16));
    if (bestLength < 2)
        return hex.join(':');
    return `${hex.slice(0, bestStart).join(':')}::${hex.slice(bestStart + bestLength).join(':')}`;
}
export function formatAddress(address) {
    return address.family === 4 ? formatIPv4(address.value) : formatIPv6(address.value);
}
/** Cách viết chuẩn của một mạng; một địa chỉ đơn (/32, /128) thì không ghi tiền tố. */
export function formatNetwork(network) {
    const base = formatAddress(network);
    return network.prefix === WIDTH[network.family] ? base : `${base}/${network.prefix}`;
}
/** Chuẩn hoá chữ người dùng gõ; `null` = không phải mạng hợp lệ. */
export function normalizeNetwork(text) {
    const network = parseNetwork(text);
    return network ? formatNetwork(network) : null;
}
/** Địa chỉ có nằm trong một trong các mạng không. Mạng hỏng thì bỏ qua (không bao giờ thành «mọi nơi»). */
export function addressInNetworks(address, networks) {
    const parsed = parseAddress(address);
    if (!parsed)
        return false;
    return networks.some((entry) => {
        const network = parseNetwork(entry);
        return (network !== null &&
            network.family === parsed.family &&
            (parsed.value & maskOf(network.family, network.prefix)) === network.value);
    });
}
/**
 * Mạng nên khai khi đang đứng ở một địa chỉ: IPv4 thì chính địa chỉ ấy (cả văn phòng ra ngoài qua nó), IPv6
 * thì cả dải /64 của mạng nội bộ (mỗi máy một địa chỉ riêng, tự đổi theo giờ, nhưng chung dải /64).
 */
export function suggestedNetwork(address) {
    const parsed = parseAddress(address);
    if (!parsed)
        return null;
    const prefix = parsed.family === 4 ? 32 : 64;
    return formatNetwork({ ...parsed, prefix, value: parsed.value & maskOf(parsed.family, prefix) });
}
/**
 * Dải KHÔNG BAO GIỜ ra tới Internet: mạng nội bộ (10.x, 172.16–31.x, 192.168.x — địa chỉ bộ phát Wi-Fi và các
 * máy trong văn phòng), máy tự gọi chính nó, mạng tự cấp, dải dùng chung của nhà mạng, và các dải tương ứng của
 * IPv6. Máy chủ chỉ thấy địa chỉ CÔNG CỘNG của đường mạng văn phòng, nên một mạng trong các dải này không máy
 * nào khớp được: 05/10/2026 khai «172.31.7.254» (địa chỉ bộ phát Wi-Fi) là cả công ty bị chặn chấm công.
 */
const INTERNAL_NETWORKS = [
    '10.0.0.0/8',
    '172.16.0.0/12',
    '192.168.0.0/16',
    '127.0.0.0/8',
    '169.254.0.0/16',
    '100.64.0.0/10',
    '0.0.0.0/8',
    '::1/128',
    '::/128',
    'fc00::/7',
    'fe80::/10',
];
/** Mạng (hay địa chỉ) nằm trong dải nội bộ — máy chủ trên Internet không bao giờ thấy nó. */
export function isInternalNetwork(text) {
    const network = parseNetwork(text);
    return network !== null && addressInNetworks(formatAddress(network), INTERNAL_NETWORKS);
}
/** Địa chỉ viết chuẩn để hiện cho người dùng; chữ lạ thì giữ nguyên. */
export function displayAddress(address) {
    const parsed = parseAddress(address);
    return parsed ? formatAddress(parsed) : address;
}
//# sourceMappingURL=networks.js.map